/** The associations matrix's geometry and shared cell styles (one place, so view and edit
 *  mode draw the same box — DESIGN.md Zero-Re-Layout). */
import { alpha, type Theme } from '@mui/material/styles';

import { CHANGED_TINT } from '@/features/records/fields/FieldSlot';

/** Category column (sticky left). */
export const CATEGORY_PX = 200;
/** Tags (min–max) column. */
export const TAGS_PX = 64;
/** One flag column. */
export const FLAG_PX = 34;
/** Row actions (sticky right): four 26 px slots and padding. */
export const ACTIONS_PX = 112;
/** A body row. */
export const ROW_PX = 34;
/** The group band. */
export const BAND_PX = 32;
/** The vertical label's room (long labels wrap to a second line). */
export const LABEL_PX = 100;
/** The count line under a label. */
export const COUNT_PX = 18;

export const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

export const tableWidth = (flagCount: number) =>
  CATEGORY_PX + TAGS_PX + flagCount * FLAG_PX + ACTIONS_PX;

/** A tint drawn over a cell's own (opaque) background — sticky cells must stay opaque. */
export const layer = (tint: (t: Theme) => string) => ({
  backgroundImage: (t: Theme) => `linear-gradient(${tint(t)}, ${tint(t)})`,
});

export const AMBER_LAYER = layer(CHANGED_TINT);

/** The crosshair: the hovered / focused cell's row and column (an inset shadow, so it sits
 *  over any tint and under the content). */
export const HIGHLIGHT = (t: Theme) => `inset 0 0 0 9999px ${alpha(t.palette.primary.main, 0.06)}`;

/** A focus stop that is a cell. */
export const CELL_FOCUS = {
  outline: 'none',
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
} as const;

/** The amber corner mark of a changed cell (a shape beside the tint, never colour alone). */
export const CHANGED_MARK = {
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 3,
    right: 3,
    width: 5,
    height: 5,
    borderRadius: '50%',
    bgcolor: 'warning.main',
  },
} as const;

export { CHANGED_TINT };
